import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import OptimizedImage from '../common/OptimizedImage';
import ApplicationLink from '../common/ApplicationLink';
import { useAuth } from '../../context/AuthContext';
import { useApplicationContext } from '../../context/ApplicationContext';
import notificationsApi from '../../services/api/notifications';
import { toModernAppSectionUrl } from '../../utils/paths';
import ThemeToggle from './ThemeToggle';
import HeaderContentVisibilityChoices from './HeaderContentVisibilityChoices';
const ROLE_LABELS = {
    reader: 'Reader',
    contributor: 'Contributor',
    screener: 'Screener',
    reviewer: 'Reviewer',
    admin: 'Admin',
};
const DEFAULT_SECTIONS = [
    {
        title: 'Debates',
        iconClass: 'fa fa-commenting',
        url: '/topics/entry/debates-discussions',
    },
    {
        title: 'Dictionary',
        iconClass: 'glyphicon glyphicon-font',
        url: '/topics/entry/dictionary',
    },
    {
        title: 'Manuscripts',
        iconClass: 'fa fa-book',
        url: '/topics/entry/sacred-texts',
    },
];
const Header = ({ onToggleSidebar, onCloseSidebar, sidebarOpen = false, focused = false, }) => {
    const location = useLocation();
    const { user, activeRole, setActiveRole, availableRoles } = useAuth();
    const { application, applicationPath } = useApplicationContext();
    const [isMoreOpen, setIsMoreOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
    const [unreadNotifications, setUnreadNotifications] = useState(0);
    const moreMenuRef = useRef(null);
    const userMenuRef = useRef(null);
    const headerSections = application?.sections?.length
        ? application.sections
        : DEFAULT_SECTIONS;
    useEffect(() => {
        let isMounted = true;
        if (!user) {
            setUnreadNotifications(0);
        }
        else {
            void notificationsApi.summary()
                .then((summary) => {
                if (isMounted)
                    setUnreadNotifications(Number(summary.unreadCount || 0));
            })
                .catch(() => {
                if (isMounted)
                    setUnreadNotifications(0);
            });
        }
        return () => {
            isMounted = false;
        };
    }, [user]);
    useEffect(() => {
        setIsMoreOpen(false);
        setIsUserMenuOpen(false);
        setIsMobileNavOpen(false);
    }, [location.pathname]);
    useEffect(() => {
        if (sidebarOpen) {
            setIsMoreOpen(false);
            setIsUserMenuOpen(false);
            setIsMobileNavOpen(false);
        }
    }, [sidebarOpen]);
    useEffect(() => {
        if (!isMoreOpen && !isUserMenuOpen) {
            return;
        }
        const handleOutsidePointer = (event) => {
            const target = event.target;
            if (isMoreOpen && !moreMenuRef.current?.contains(target)) {
                setIsMoreOpen(false);
            }
            if (isUserMenuOpen && !userMenuRef.current?.contains(target)) {
                setIsUserMenuOpen(false);
            }
        };
        document.addEventListener('pointerdown', handleOutsidePointer);
        return () => document.removeEventListener('pointerdown', handleOutsidePointer);
    }, [isMoreOpen, isUserMenuOpen]);
    const toggleMobileNavigation = () => {
        const shouldOpen = !isMobileNavOpen;
        setIsMobileNavOpen(shouldOpen);
        if (shouldOpen) {
            setIsMoreOpen(false);
            setIsUserMenuOpen(false);
            onCloseSidebar?.();
        }
    };
    const toggleMoreMenu = () => {
        const shouldOpen = !isMoreOpen;
        setIsMoreOpen(shouldOpen);
        if (shouldOpen) {
            setIsUserMenuOpen(false);
            setIsMobileNavOpen(false);
            onCloseSidebar?.();
        }
    };
    const toggleUserMenu = () => {
        const shouldOpen = !isUserMenuOpen;
        setIsUserMenuOpen(shouldOpen);
        if (shouldOpen) {
            setIsMoreOpen(false);
            setIsMobileNavOpen(false);
            onCloseSidebar?.();
        }
    };
    return (React.createElement("div", { className: `navbar navbar-default navbar-fixed-top${focused ? ' wt-auth-header' : ''}` },
        React.createElement("div", { className: "container-fluid" },
            React.createElement("div", { className: "navbar-header" },
                React.createElement(ApplicationLink, { href: application?.homeUrl || '/', className: "navbar-brand" },
                    React.createElement(OptimizedImage, { src: application?.logoIcon || '/img/logo-64x64.png', alt: "Logo", width: 32, height: 32, className: "navbar-logo" }),
                    React.createElement("span", { className: "navbar-brand-label" }, focused ? (application?.navTitle || application?.name || application?.title || 'Wikitruth') : (React.createElement("span", { className: "hidden-xxs" }, application?.navTitle || application?.name || application?.title || 'Wikitruth')))),
                !focused ? (React.createElement("button", { type: "button", className: "navbar-toggle collapsed", "aria-label": "Toggle navigation", "aria-expanded": isMobileNavOpen, "aria-controls": "header-main-collapse", onClick: toggleMobileNavigation },
                    React.createElement("span", { className: "sr-only" }, "Toggle navigation"),
                    React.createElement("i", { className: "fa fa-navicon", "aria-hidden": "true" }))) : null),
            focused ? (React.createElement("div", { className: "wt-auth-header-tools" },
                React.createElement(ThemeToggle, { focused: true }))) : null,
            !focused ? (React.createElement("div", { id: "header-main-collapse", className: `navbar-collapse my-navbar-collapse collapse${isMobileNavOpen ? ' in' : ''}` },
                React.createElement("nav", { className: "wt-primary-navigation", "aria-label": "Primary navigation" },
                    React.createElement("ul", { className: "nav navbar-nav" },
                        React.createElement("li", null,
                            React.createElement(ApplicationLink, { href: applicationPath(application?.exploreUrl || '/explore'), title: "Explore", "aria-label": "Explore topics", onClick: () => setIsMobileNavOpen(false) },
                                React.createElement("i", { className: "fa fa-globe" }),
                                React.createElement("span", { className: "hidden-xs" }, " Explore"))),
                        React.createElement("li", null,
                            React.createElement(Link, { to: applicationPath('/search'), title: "Search", "aria-label": "Search content", onClick: () => setIsMobileNavOpen(false) },
                                React.createElement("i", { className: "fa fa-search" }),
                                React.createElement("span", { className: "hidden-xs" }, " Search"))),
                        React.createElement("li", { ref: moreMenuRef, className: `dropdown ${isMoreOpen ? 'open' : ''}` },
                            React.createElement("button", { type: "button", title: "more", className: "dropdown-toggle btn btn-link navbar-btn", "aria-label": "More navigation options", "aria-haspopup": "true", "aria-expanded": isMoreOpen, "aria-controls": "header-more-menu", onClick: toggleMoreMenu, onKeyDown: (event) => {
                                    if (event.key === 'Escape') {
                                        setIsMoreOpen(false);
                                    }
                                } },
                                React.createElement("span", { className: "glyphicon glyphicon-option-horizontal", "aria-hidden": "true" })),
                            React.createElement("ul", { id: "header-more-menu", className: "dropdown-menu dropdown-menu-right wt-header-more-menu" },
                                React.createElement("li", { className: "dropdown-header" }, "more"),
                                !user ? (React.createElement("li", { className: "visible-xs" },
                                    React.createElement(Link, { to: applicationPath('/contribute'), onClick: () => {
                                            setIsMoreOpen(false);
                                            setIsMobileNavOpen(false);
                                        } },
                                        React.createElement("i", { className: "fa fa-user-secret" }),
                                        " Contribute anonymously"))) : null,
                                React.createElement("li", null,
                                    React.createElement(Link, { to: applicationPath('/create'), onClick: () => {
                                            setIsMoreOpen(false);
                                            setIsMobileNavOpen(false);
                                        } },
                                        React.createElement("i", { className: "fa fa-plus-circle" }),
                                        " Create")),
                                React.createElement("li", null,
                                    React.createElement(Link, { to: applicationPath('/groups'), onClick: () => {
                                            setIsMoreOpen(false);
                                            setIsMobileNavOpen(false);
                                        } },
                                        React.createElement("i", { className: "fa fa-group" }),
                                        " Groups")),
                                React.createElement("li", null,
                                    React.createElement(Link, { to: applicationPath('/members'), onClick: () => {
                                            setIsMoreOpen(false);
                                            setIsMobileNavOpen(false);
                                        } },
                                        React.createElement("i", { className: "fa fa-user-circle" }),
                                        " Members")),
                                !user ? (React.createElement(React.Fragment, null,
                                    React.createElement("li", { className: "divider", "aria-hidden": "true" }),
                                    React.createElement(HeaderContentVisibilityChoices, { onSelected: () => setIsMoreOpen(false) }))) : null,
                                React.createElement("li", { className: "divider", "aria-hidden": "true" }),
                                headerSections.map((section) => (React.createElement("li", { key: `${section.title}-${section.url || ''}` },
                                    React.createElement(ApplicationLink, { href: applicationPath(toModernAppSectionUrl(section.url)), title: section.description, onClick: () => {
                                            setIsMoreOpen(false);
                                            setIsMobileNavOpen(false);
                                        } },
                                        React.createElement("i", { className: section.iconClass || 'fa fa-folder-open', "aria-hidden": "true" }),
                                        ' ',
                                        section.title)))),
                                React.createElement("li", { className: "divider", "aria-hidden": "true" }),
                                React.createElement("li", null,
                                    React.createElement(Link, { to: applicationPath('/transparency'), onClick: () => {
                                            setIsMoreOpen(false);
                                            setIsMobileNavOpen(false);
                                        } },
                                        React.createElement("i", { className: "fa fa-shield" }),
                                        " Transparency & Trust")),
                                React.createElement("li", null,
                                    React.createElement(ApplicationLink, { href: applicationPath(toModernAppSectionUrl(application?.aboutUrl || '/about')), onClick: () => {
                                            setIsMoreOpen(false);
                                            setIsMobileNavOpen(false);
                                        } },
                                        React.createElement("i", { className: "fa fa-info-circle" }),
                                        " About")),
                                React.createElement("li", null,
                                    React.createElement(Link, { to: applicationPath('/contact'), onClick: () => {
                                            setIsMoreOpen(false);
                                            setIsMobileNavOpen(false);
                                        } },
                                        React.createElement("i", { className: "fa fa-comment" }),
                                        " Contact")),
                                React.createElement("li", { role: "separator", className: "divider", "aria-hidden": "true" }),
                                React.createElement("li", null,
                                    React.createElement("a", { href: "/legacy/", onClick: () => {
                                            setIsMoreOpen(false);
                                            setIsMobileNavOpen(false);
                                        } },
                                        React.createElement("i", { className: "fa fa-history" }),
                                        " Legacy UX")))))),
                React.createElement("nav", { "aria-label": "Account navigation" },
                    React.createElement("ul", { className: "nav navbar-nav navbar-right wt-account-nav" },
                        React.createElement("li", { className: "wt-theme-toggle-item" },
                            React.createElement(ThemeToggle, null)),
                        user ? (React.createElement(React.Fragment, null,
                            React.createElement("li", null,
                                React.createElement(Link, { to: applicationPath('/notifications'), title: "Notifications", "aria-label": "Notifications" },
                                    React.createElement("i", { className: "fa fa-bell-o" }),
                                    unreadNotifications > 0 ? (React.createElement("span", { className: "badge", style: { marginLeft: 6, background: '#d9534f' } }, unreadNotifications > 99 ? '99+' : unreadNotifications)) : null)),
                            React.createElement("li", { ref: userMenuRef, className: `dropdown ${isUserMenuOpen ? 'open' : ''}` },
                                React.createElement("button", { type: "button", className: "dropdown-toggle wt-navbar-control", "aria-label": `Account menu for ${user.username}`, "aria-haspopup": "true", "aria-expanded": isUserMenuOpen, "aria-controls": "header-user-menu", onClick: toggleUserMenu, onKeyDown: (event) => {
                                        if (event.key === 'Escape') {
                                            setIsUserMenuOpen(false);
                                        }
                                    } },
                                    React.createElement("span", { className: "hidden-xxs-x" },
                                        React.createElement("i", { className: "fa fa-user-circle" }),
                                        ' ',
                                        React.createElement("span", { className: "hidden-xxs" },
                                            user.username,
                                            " "),
                                        React.createElement("span", { className: "caret" }))),
                                React.createElement("ul", { id: "header-user-menu", className: "dropdown-menu dropdown-menu-right" },
                                    React.createElement("li", { className: "dropdown-header" }, "Account"),
                                    React.createElement("li", null,
                                        React.createElement(Link, { to: applicationPath(`/members/${user.username}`), onClick: () => {
                                                setIsUserMenuOpen(false);
                                                setIsMobileNavOpen(false);
                                            } },
                                            React.createElement("i", { className: "fa fa-user-circle" }),
                                            " My Profile")),
                                    React.createElement("li", null,
                                        React.createElement(Link, { to: applicationPath(`/members/${user.username}/journal`), onClick: () => {
                                                setIsUserMenuOpen(false);
                                                setIsMobileNavOpen(false);
                                            } },
                                            React.createElement("i", { className: "fa fa-folder-open" }),
                                            " My Journal")),
                                    Boolean(user.roles?.admin) && (React.createElement("li", null,
                                        React.createElement(Link, { to: applicationPath('/admin'), onClick: () => {
                                                setIsUserMenuOpen(false);
                                                setIsMobileNavOpen(false);
                                            } },
                                            React.createElement("i", { className: "fa fa-gear" }),
                                            " Admin Area"))),
                                    availableRoles.length > 1 && (React.createElement(React.Fragment, null,
                                        React.createElement("li", { className: "divider", "aria-hidden": "true" }),
                                        React.createElement("li", { className: "dropdown-header" }, "Switch Role"),
                                        availableRoles.map((role) => (React.createElement("li", { key: role },
                                            React.createElement("button", { type: "button", className: "btn btn-link", style: { width: '100%', textAlign: 'left', padding: '3px 20px' }, onClick: () => {
                                                    setActiveRole(role);
                                                    setIsUserMenuOpen(false);
                                                    setIsMobileNavOpen(false);
                                                } },
                                                React.createElement("i", { className: `fa ${activeRole === role ? 'fa-check-circle' : 'fa-circle-o'}` }),
                                                ' ',
                                                ROLE_LABELS[role] || role)))))),
                                    React.createElement("li", { className: "divider", "aria-hidden": "true" }),
                                    React.createElement(HeaderContentVisibilityChoices, { onSelected: () => setIsUserMenuOpen(false) }),
                                    React.createElement("li", { className: "divider", "aria-hidden": "true" }),
                                    React.createElement("li", null,
                                        React.createElement(Link, { to: applicationPath('/logout'), onClick: () => {
                                                setIsUserMenuOpen(false);
                                                setIsMobileNavOpen(false);
                                            } },
                                            React.createElement("i", { className: "fa fa-sign-out" }),
                                            " Sign Out")))))) : (React.createElement(React.Fragment, null,
                            React.createElement("li", { className: "hidden-xs" },
                                React.createElement(Link, { to: applicationPath('/contribute'), className: "nav-narrow", "aria-label": "Contribute anonymously" },
                                    React.createElement("i", { className: "fa fa-user-secret" }),
                                    React.createElement("span", { className: "hidden-xs" }, " Contribute"))),
                            location.pathname !== applicationPath('/login') ? (React.createElement("li", null,
                                React.createElement(Link, { to: applicationPath('/login'), className: "nav-narrow", "aria-label": "Sign in" },
                                    React.createElement("i", { className: "fa fa-user" }),
                                    React.createElement("span", { className: "hidden-xxxxs" }, " Sign In")))) : null)),
                        onToggleSidebar ? (React.createElement("li", { className: "dropdown visible-sm visible-xs" },
                            React.createElement("button", { type: "button", className: "dropdown-toggle btn btn-link navbar-btn", "aria-label": sidebarOpen ? 'Close sidebar' : 'Open sidebar', "aria-expanded": sidebarOpen, "aria-controls": "sidebar", onClick: (event) => {
                                    event.preventDefault();
                                    setIsMoreOpen(false);
                                    setIsUserMenuOpen(false);
                                    setIsMobileNavOpen(false);
                                    onToggleSidebar();
                                }, style: { color: '#777' } },
                                React.createElement("i", { className: "fa fa-navicon", "aria-hidden": "true" })))) : null)))) : null)));
};
export default Header;
